import {
  Accordion, Alert, Anchor, Badge, Button, Card, Chip, Divider, Group, List, Modal, NumberInput, Paper, Select, SimpleGrid, Stack,
  Switch, Table, Tabs, Text, Textarea, TextInput, ThemeIcon, Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconBook2, IconCheck, IconClipboardList, IconCopy, IconEdit, IconFileCheck, IconHelp, IconListSearch,
  IconQuestionMark, IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { errorMessage } from "../../api/client";
import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { Empty, fmtDate, fmtNum, PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import type { Producer } from "../producers/ProducersPage";

// ------------------------------------------------------------------------------------------------ tipovi

interface Sector { code: string; name: string; status: "razvijeno" | "djelimicno" | "dalji_razvoj"; description: string }
interface Regulation { code: string; name: string; gazette?: string; url?: string }
interface MGroup { code: string; name: string; sector_code: string; parent_code?: string | null; regulation_code?: string; articles?: string }
export interface Measure {
  id: number; year: number; code: string; name: string; group_code: string; regulation_code?: string; article?: string;
  sector_code: string; fruit_relevance: "direktna" | "posredna" | "nema"; evaluator?: string | null; params: Record<string, unknown>;
  beneficiaries?: string; conditions: string[]; amount?: string; maximum?: string; deadline?: string; deadline_date?: string | null;
  documents: string[]; control?: string; note?: string; active: boolean; updated_by?: string | null; updated_at?: string;
}
interface Catalog { year: number; sectors: Sector[]; regulations: Regulation[]; groups: MGroup[]; measures: Measure[] }
interface Check { label: string; ok: boolean | null; detail?: string }
interface Result {
  measure_id: number; code: string; name: string; group_code: string; sector_code: string; fruit_relevance: string;
  evaluator?: string | null; status: string; checks: Check[]; rows: Record<string, unknown>[]; estimate_km?: number | null;
  estimate_kind?: "procjena" | "gornja_granica"; estimate_note?: string; summary?: string; deadline?: string;
  deadline_date?: string | null; rok_istekao?: boolean; documents: string[]; amount?: string; maximum?: string;
  application?: { id: number; status: string } | null;
}
interface CheckResp {
  year: number; holding: { id: number; name: string; bpg: string; category?: string; rpg_status: string; opstina?: string };
  period: { od: string; do: string }; general: Check[]; area_by_species: Record<string, number>; results: Result[];
  estimate_total_km: number; investment_ceiling_km: number; disclaimer: string;
}
interface Application {
  id: number; year: number; measure_code: string; measure_name?: string; holding_id: number; holding_name: string; bpg: string;
  opstina?: string; status: string; reg_no?: string; requested_qty?: number; unit?: string; requested_km?: number;
  approved_km?: number; note?: string; submitted_at?: string; decided_at?: string; updated_at: string; created_by?: string;
  check_result?: Partial<Result> & { general?: Check[] };
}

const STATUS: Record<string, { label: string; color: string }> = {
  ispunjava: { label: "Ispunjava uslove", color: "green" },
  provjeriti: { label: "Provjeriti", color: "yellow" },
  ne_ispunjava: { label: "Ne ispunjava", color: "red" },
  nije_primjenjivo: { label: "Nije primjenjivo", color: "gray" },
  informativno: { label: "Informativno", color: "blue" },
};
export const APP_STATUS: Record<string, { label: string; color: string }> = {
  nacrt: { label: "Nacrt", color: "gray" }, podnesen: { label: "Podnesen", color: "blue" }, u_obradi: { label: "U obradi", color: "cyan" },
  kontrola: { label: "Kontrola na terenu", color: "orange" }, odobren: { label: "Odobren", color: "green" },
  odbijen: { label: "Odbijen", color: "red" }, isplacen: { label: "Isplaćen", color: "teal" },
};
const REL: Record<string, { label: string; color: string }> = {
  direktna: { label: "voćarstvo — direktno", color: "green" }, posredna: { label: "voćarstvo — posredno", color: "lime" },
  nema: { label: "nije za voćarstvo", color: "gray" },
};
const SECTOR_STATUS: Record<string, { label: string; color: string }> = {
  razvijeno: { label: "razvijeno", color: "green" }, djelimicno: { label: "djelimično (voćarstvo)", color: "yellow" },
  dalji_razvoj: { label: "trenutno nije obuhvaćeno aplikacijom", color: "gray" },
};
const THIS_YEAR = new Date().getFullYear();

function CheckIcon({ ok }: { ok: boolean | null }) {
  if (ok === true) return <ThemeIcon size="sm" radius="xl" color="green" variant="light"><IconCheck size={13} /></ThemeIcon>;
  if (ok === false) return <ThemeIcon size="sm" radius="xl" color="red" variant="light"><IconX size={13} /></ThemeIcon>;
  return <ThemeIcon size="sm" radius="xl" color="yellow" variant="light"><IconQuestionMark size={13} /></ThemeIcon>;
}

export function ChecksList({ checks }: { checks: Check[] }) {
  const { t } = useT();
  if (!checks.length) return null;
  return (
    <Stack gap={4}>
      {checks.map((c, i) => (
        <Group key={i} gap={8} wrap="nowrap" align="flex-start">
          <CheckIcon ok={c.ok} />
          <Text size="sm" style={{ flex: 1 }}>{t(c.label)}{c.detail && <Text span c="dimmed" size="sm"> — {t(c.detail)}</Text>}</Text>
        </Group>
      ))}
    </Stack>
  );
}

function useCatalog(year: number) {
  return useQuery({ queryKey: ["subsidies-catalog", year], queryFn: () => http.get<Catalog>("/api/v1/subsidies/catalog", { year }) });
}

function useYears() {
  const q = useQuery({ queryKey: ["subsidies-years"], queryFn: () => http.get<number[]>("/api/v1/subsidies/years") });
  return useMemo(() => {
    const ys = new Set([...(q.data ?? []), THIS_YEAR]);
    return [...ys].sort((a, b) => b - a).map(String);
  }, [q.data]);
}

// ------------------------------------------------------------------------------------------------ mjera (detalj i izmjena)

function MeasureDetail({ m, onClose }: { m: Measure; onClose: () => void }) {
  const { t } = useT();
  const can = useCan();
  const [edit, setEdit] = useState(false);
  return (
    <Modal opened onClose={onClose} size="xl" title={<Text fw={700}>{m.code} — {t(m.name)}</Text>}>
      {edit ? <MeasureEdit m={m} onDone={() => { setEdit(false); onClose(); }} /> : (
        <Stack gap="sm">
          <Group gap={6}>
            <Badge color={REL[m.fruit_relevance]?.color}>{t(REL[m.fruit_relevance]?.label ?? m.fruit_relevance)}</Badge>
            <Badge variant="light">{m.regulation_code} {m.article}</Badge>
            {m.evaluator ? <Badge color="teal" variant="light">{t("automatska provjera uslova")}</Badge>
              : <Badge color="gray" variant="light">{t("informativno")}</Badge>}
            {m.updated_by && <Badge color="orange" variant="outline">{t("izmijenjeno u aplikaciji")}: {m.updated_by}</Badge>}
            {!m.active && <Badge color="red">{t("neaktivna")}</Badge>}
          </Group>
          {m.beneficiaries && <><Title order={6}>{t("Korisnici")}</Title><Text size="sm">{t(m.beneficiaries)}</Text></>}
          <Title order={6}>{t("Uslovi")}</Title>
          <List size="sm" spacing={2}>{m.conditions.map((c, i) => <List.Item key={i}>{t(c)}</List.Item>)}</List>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <Paper withBorder p="xs"><Text size="xs" c="dimmed">{t("Iznos")}</Text><Text size="sm">{t(m.amount ?? "—")}</Text></Paper>
            <Paper withBorder p="xs"><Text size="xs" c="dimmed">{t("Maksimum")}</Text><Text size="sm">{t(m.maximum ?? "—")}</Text></Paper>
            <Paper withBorder p="xs"><Text size="xs" c="dimmed">{t("Rok")}</Text><Text size="sm">{t(m.deadline ?? "—")}</Text>
              {m.deadline_date && <Badge mt={4} size="sm" color={m.deadline_date < new Date().toISOString().slice(0, 10) ? "red" : "blue"}>
                {fmtDate(m.deadline_date)}</Badge>}</Paper>
          </SimpleGrid>
          <Title order={6}>{t("Dokumentacija")}</Title>
          <List size="sm" spacing={2}>{m.documents.map((c, i) => <List.Item key={i}>{t(c)}</List.Item>)}</List>
          {m.control && <><Title order={6}>{t("Kontrola")}</Title><Text size="sm">{t(m.control)}</Text></>}
          {m.note && <Alert color="gray" variant="light" title={t("Napomena")}>{t(m.note)}</Alert>}
          {m.evaluator && (
            <Accordion variant="contained">
              <Accordion.Item value="p">
                <Accordion.Control icon={<IconListSearch size={16} />}>{t("Parametri provjere uslova")} ({m.evaluator})</Accordion.Control>
                <Accordion.Panel><pre style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(m.params, null, 2)}</pre></Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}
          {can("subsidies:manage") && (
            <Group justify="flex-end"><Button leftSection={<IconEdit size={16} />} variant="light" onClick={() => setEdit(true)}>{t("Izmijeni")}</Button></Group>
          )}
        </Stack>
      )}
    </Modal>
  );
}

function MeasureEdit({ m, onDone }: { m: Measure; onDone: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [v, setV] = useState({
    amount: m.amount ?? "", maximum: m.maximum ?? "", deadline: m.deadline ?? "", deadline_date: m.deadline_date ?? "",
    note: m.note ?? "", conditions: m.conditions.join("\n"), documents: m.documents.join("\n"),
    params: JSON.stringify(m.params, null, 2), active: m.active,
  });
  let paramsOk = true;
  try { JSON.parse(v.params || "{}"); } catch { paramsOk = false; }
  const save = useMutation({
    mutationFn: () => http.patch(`/api/v1/subsidies/measures/${m.id}`, {
      amount: v.amount, maximum: v.maximum, deadline: v.deadline, deadline_date: v.deadline_date || null, note: v.note,
      conditions: v.conditions.split("\n").map((x) => x.trim()).filter(Boolean),
      documents: v.documents.split("\n").map((x) => x.trim()).filter(Boolean),
      params: JSON.parse(v.params || "{}"), active: v.active,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["subsidies-catalog"] }); notifications.show({ color: "green", message: t("Mjera sačuvana") }); onDone(); },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });
  return (
    <Stack gap="sm">
      <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
        {t("Izmijenjena mjera se više ne usklađuje automatski sa referentnim podacima Pravilnika. Mijenjajte kad Agencija objavi izmjenu (npr. novi iznos ili rok).")}
      </Alert>
      <Textarea label={t("Iznos")} autosize minRows={2} value={v.amount} onChange={(e) => setV({ ...v, amount: e.currentTarget.value })} />
      <Textarea label={t("Maksimum")} autosize minRows={1} value={v.maximum} onChange={(e) => setV({ ...v, maximum: e.currentTarget.value })} />
      <Group grow>
        <TextInput label={t("Rok (tekst)")} value={v.deadline} onChange={(e) => setV({ ...v, deadline: e.currentTarget.value })} />
        <TextInput label={t("Krajnji rok (datum)")} type="date" value={v.deadline_date} onChange={(e) => setV({ ...v, deadline_date: e.currentTarget.value })} />
      </Group>
      <Textarea label={t("Uslovi (jedan po redu)")} autosize minRows={3} value={v.conditions} onChange={(e) => setV({ ...v, conditions: e.currentTarget.value })} />
      <Textarea label={t("Dokumentacija (jedna stavka po redu)")} autosize minRows={3} value={v.documents} onChange={(e) => setV({ ...v, documents: e.currentTarget.value })} />
      <Textarea label={t("Napomena")} autosize minRows={1} value={v.note} onChange={(e) => setV({ ...v, note: e.currentTarget.value })} />
      {m.evaluator && (
        <Textarea label={t("Parametri provjere uslova (JSON)")} description={t("Pragovi (min_ha, min_kg), iznosi (km_kg, km_ha), normativi prinosa, limiti po kategoriji gazdinstva")}
          autosize minRows={6} maxRows={18} styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
          error={paramsOk ? undefined : t("Neispravan JSON")} value={v.params} onChange={(e) => setV({ ...v, params: e.currentTarget.value })} />
      )}
      <Switch label={t("Mjera aktivna")} checked={v.active} onChange={(e) => setV({ ...v, active: e.currentTarget.checked })} />
      <Group justify="flex-end">
        <Button variant="default" onClick={onDone}>{t("Odustani")}</Button>
        <Button disabled={!paramsOk} loading={save.isPending} onClick={() => save.mutate()}>{t("Sačuvaj")}</Button>
      </Group>
    </Stack>
  );
}

// ------------------------------------------------------------------------------------------------ kartica: mjere

function MeasuresTab({ year }: { year: number }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const cat = useCatalog(year);
  const [sector, setSector] = useState<string[]>([]);
  const [onlyFruit, setOnlyFruit] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<Measure | null>(null);
  const [copyTo, setCopyTo] = useState<number | null>(null);
  const copy = useMutation({
    mutationFn: () => http.post<{ copied: number; year: number }>("/api/v1/subsidies/copy-year", { from_year: year, to_year: copyTo }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["subsidies-years"] });
      notifications.show({ color: "green", message: `${t("Kopirano mjera")}: ${r.copied} → ${r.year}` }); setCopyTo(null);
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });
  if (cat.isLoading) return <Text c="dimmed">{t("Učitavanje…")}</Text>;
  const c = cat.data;
  if (!c || !c.measures.length) return <Empty text={`Nema mjera za ${year}. godinu — kopirajte mjere iz prethodne godine.`} />;
  const secName = Object.fromEntries(c.sectors.map((s) => [s.code, s]));
  const qq = q.trim().toLowerCase();
  const visible = c.measures.filter((m) => (!sector.length || sector.includes(m.sector_code))
    && (!onlyFruit || m.fruit_relevance !== "nema") && (!qq || `${m.code} ${m.name}`.toLowerCase().includes(qq)));
  const byGroup = new Map<string, Measure[]>();
  visible.forEach((m) => byGroup.set(m.group_code, [...(byGroup.get(m.group_code) ?? []), m]));
  const counts = (code: string) => c.measures.filter((m) => m.sector_code === code).length;

  return (
    <Stack>
      <Card withBorder>
        <Group justify="space-between" mb="xs">
          <Title order={5}>{t("Opšta klasifikacija po sektorima")}</Title>
          <Text size="xs" c="dimmed">{t("Klik na sektor filtrira mjere")}</Text>
        </Group>
        <Chip.Group multiple value={sector} onChange={setSector}>
          <Group gap="xs">
            {c.sectors.map((s) => (
              <Tooltip key={s.code} label={t(s.description)} multiline w={300}>
                <div><Chip value={s.code} color={SECTOR_STATUS[s.status].color} variant="light">
                  {t(s.name)} · {counts(s.code)} — {t(SECTOR_STATUS[s.status].label)}
                </Chip></div>
              </Tooltip>
            ))}
          </Group>
        </Chip.Group>
      </Card>
      <Group justify="space-between">
        <Group>
          <TextInput placeholder={t("Pretraga: šifra ili naziv mjere")} value={q} onChange={(e) => setQ(e.currentTarget.value)} w={300} />
          <Switch label={t("Samo mjere za voćarstvo")} checked={onlyFruit} onChange={(e) => setOnlyFruit(e.currentTarget.checked)} />
          <Text size="sm" c="dimmed">{visible.length} / {c.measures.length} {t("mjera")}</Text>
        </Group>
        {can("subsidies:manage") && (
          <Group gap="xs">
            <NumberInput size="xs" w={110} placeholder={t("nova godina")} value={copyTo ?? ""} min={2020} max={2100}
              onChange={(v) => setCopyTo(typeof v === "number" ? v : null)} />
            <Button size="xs" variant="light" leftSection={<IconCopy size={14} />} disabled={!copyTo} loading={copy.isPending}
              onClick={() => copy.mutate()}>{t(`Kopiraj ${year}. u novu godinu`)}</Button>
          </Group>
        )}
      </Group>
      {c.regulations.filter((r) => ["P1", "P2"].includes(r.code)).map((r) => {
        const groups = c.groups.filter((g) => g.regulation_code === r.code && (byGroup.get(g.code)?.length ?? 0) > 0);
        if (!groups.length) return null;
        return (
          <Card key={r.code} withBorder>
            <Text fw={700}>{r.code}: {t(r.name)}</Text>
            <Text size="xs" c="dimmed" mb="sm">{r.gazette}{r.url && <> · <Anchor href={r.url} target="_blank" size="xs">{t("izvor")}</Anchor></>}</Text>
            <Accordion multiple variant="separated" defaultValue={groups.filter((g) => byGroup.get(g.code)!.some((m) => m.fruit_relevance === "direktna")).map((g) => g.code)}>
              {groups.map((g) => (
                <Accordion.Item key={g.code} value={g.code}>
                  <Accordion.Control>
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={600} size="sm">{t(g.name)}</Text>
                      <Badge size="xs" variant="light" color={SECTOR_STATUS[secName[g.sector_code]?.status ?? "dalji_razvoj"]?.color}>
                        {t(secName[g.sector_code]?.name ?? g.sector_code)}</Badge>
                      <Badge size="xs" variant="outline" color="gray">{byGroup.get(g.code)!.length}</Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Table striped highlightOnHover verticalSpacing={6}>
                      <Table.Tbody>
                        {byGroup.get(g.code)!.map((m) => (
                          <Table.Tr key={m.id} style={{ cursor: "pointer", opacity: m.active ? 1 : 0.5 }} onClick={() => setOpen(m)}>
                            <Table.Td w={80}><Text size="sm" fw={600}>{m.code}</Text></Table.Td>
                            <Table.Td><Text size="sm">{t(m.name)}</Text>
                              <Text size="xs" c="dimmed" lineClamp={1}>{t(m.amount ?? "")}</Text></Table.Td>
                            <Table.Td w={330}>
                              <Group gap={4} justify="flex-end">
                                <Badge size="xs" color={REL[m.fruit_relevance].color} variant={m.fruit_relevance === "nema" ? "outline" : "light"}>
                                  {t(REL[m.fruit_relevance].label)}</Badge>
                                {m.evaluator && <Badge size="xs" color="teal">{t("provjera uslova")}</Badge>}
                                {secName[m.sector_code]?.status === "dalji_razvoj" && <Badge size="xs" color="gray" variant="dot">{t("trenutno nije obuhvaćeno aplikacijom")}</Badge>}
                                {m.updated_by && <Badge size="xs" color="orange" variant="outline">{t("izmijenjeno")}</Badge>}
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card>
        );
      })}
      {open && <MeasureDetail m={open} onClose={() => setOpen(null)} />}
    </Stack>
  );
}

// ------------------------------------------------------------------------------------------------ kartica: provjera uslova

function RowsTable({ rows }: { rows: Record<string, unknown>[] }) {
  const { t } = useT();
  if (!rows.length) return null;
  const LABEL: Record<string, string> = {
    vocnjak: "Voćnjak", vrsta: "Vrsta", povrsina_ha: "ha", prodato_kg: "Prodato kg", priznato_kg: "Priznato kg", km_kg: "KM/kg",
    iznos_km: "Iznos KM", min_ha: "Min ha", min_kg: "Min kg", max_prinos: "Normativ kg/ha", sadnica: "Sadnica",
    godina_sadnje: "Sadnja", gustina: "Gustina/ha", min_gustina: "Min gustina", certifikovan_materijal: "Cert. materijal",
    udio_pct: "Udio %", gornja_granica_km: "Gornja granica KM", navodnjavanje: "Navodnjavanje", status: "Status",
    napomena: "Napomena", dokument: "Dokument", naziv: "Naziv", datum: "Datum", prodaja_bez_dokumenta: "Bez dokumenta",
  };
  const keys = Object.keys(rows[0]).filter((k) => k !== "korekcija" && LABEL[k]);
  return (
    <Table.ScrollContainer minWidth={500}>
      <Table withTableBorder fz="xs" verticalSpacing={3}>
        <Table.Thead><Table.Tr>{keys.map((k) => <Table.Th key={k}>{t(LABEL[k])}</Table.Th>)}</Table.Tr></Table.Thead>
        <Table.Tbody>
          {rows.map((r, i) => (
            <Table.Tr key={i}>
              {keys.map((k) => {
                const v = r[k];
                if (k === "status") return <Table.Td key={k}><Badge size="xs" color={STATUS[String(v)]?.color ?? "gray"}>{t(STATUS[String(v)]?.label ?? String(v))}</Badge></Table.Td>;
                if (typeof v === "boolean") return <Table.Td key={k}>{v ? t("da") : t("ne")}</Table.Td>;
                if (typeof v === "number") return <Table.Td key={k} ta="right">{fmtNum(v, k.endsWith("_ha") || k === "km_kg" ? 2 : 0)}</Table.Td>;
                return <Table.Td key={k}>{v == null || v === "" ? "—" : t(String(v))}</Table.Td>;
              })}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function ResultCard({ r, year, holdingId }: { r: Result; year: number; holdingId: number }) {
  const { t } = useT();
  const qc = useQueryClient();
  const st = STATUS[r.status] ?? STATUS.informativno;
  const create = useMutation({
    mutationFn: () => http.post<Application>("/api/v1/subsidies/applications", { year, measure_code: r.code, holding_id: holdingId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subsidies-check"] }); qc.invalidateQueries({ queryKey: ["subsidies-apps"] });
      notifications.show({ color: "green", message: t("Zahtjev evidentiran kao nacrt (kartica Zahtjevi)") });
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });
  return (
    <Card withBorder style={{ borderLeft: `5px solid var(--mantine-color-${st.color}-6)` }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          <Group gap={6}>
            <Text fw={700}>{r.code}</Text>
            <Badge color={st.color}>{t(st.label)}</Badge>
            {r.rok_istekao && <Badge color="red" variant="outline">{t("rok za podnošenje istekao")}</Badge>}
            {r.deadline_date && !r.rok_istekao && <Badge color="blue" variant="outline">{t("rok")} {fmtDate(r.deadline_date)}</Badge>}
          </Group>
          <Text size="sm">{t(r.name)}</Text>
        </div>
        {r.estimate_km != null && r.status !== "nije_primjenjivo" && r.status !== "informativno" && (
          <Paper withBorder px="sm" py={4} ta="right">
            <Text size="xs" c="dimmed">{t(r.estimate_kind === "gornja_granica" ? "Gornja granica" : "Procjena")}</Text>
            <Text fw={800} size="lg">{fmtNum(r.estimate_km, 2)} KM</Text>
          </Paper>
        )}
      </Group>
      {r.summary && <Text size="sm" c="dimmed" mt={6}>{t(r.summary)}</Text>}
      {r.checks.length > 0 && <><Divider my="xs" /><ChecksList checks={r.checks} /></>}
      {r.rows.length > 0 && <Stack mt="xs" gap={4}><RowsTable rows={r.rows} />
        {r.rows.filter((x) => x.korekcija).map((x, i) => <Text key={i} size="xs" c="orange">{t(String(x.korekcija))}</Text>)}</Stack>}
      {r.estimate_note && <Text size="xs" c="dimmed" mt={6}>{t(r.estimate_note)}</Text>}
      <Accordion variant="default" mt={4}>
        <Accordion.Item value="d">
          <Accordion.Control icon={<IconFileCheck size={16} />}><Text size="sm">{t("Potrebna dokumentacija i iznos po Pravilniku")}</Text></Accordion.Control>
          <Accordion.Panel>
            <List size="sm" spacing={2}>{r.documents.map((d, i) => <List.Item key={i}>{t(d)}</List.Item>)}</List>
            {r.amount && <Text size="sm" mt="xs"><b>{t("Iznos")}:</b> {t(r.amount)}</Text>}
            {r.maximum && <Text size="sm"><b>{t("Maksimum")}:</b> {t(r.maximum)}</Text>}
            {r.deadline && <Text size="sm"><b>{t("Rok")}:</b> {t(r.deadline)}</Text>}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      <Group justify="flex-end" mt="xs">
        {r.application ? (
          <Badge size="lg" variant="light" color={APP_STATUS[r.application.status]?.color}>
            {t("Zahtjev")} #{r.application.id}: {t(APP_STATUS[r.application.status]?.label ?? r.application.status)}</Badge>
        ) : r.status !== "nije_primjenjivo" && (
          <Button size="xs" variant="light" leftSection={<IconClipboardList size={14} />} loading={create.isPending}
            onClick={() => create.mutate()}>{t("Evidentiraj zahtjev")}</Button>
        )}
      </Group>
    </Card>
  );
}

function CheckTab({ year, holdingId, setHoldingId }: { year: number; holdingId: number | null; setHoldingId: (v: number | null) => void }) {
  const { t } = useT();
  const [showInfo, setShowInfo] = useState(false);
  const holdings = useQuery({ queryKey: ["producers-all"], queryFn: () => http.get<Producer[]>("/api/v1/producers", { limit: 2000 }) });
  const chk = useQuery({
    queryKey: ["subsidies-check", holdingId, year], enabled: !!holdingId,
    queryFn: () => http.get<CheckResp>(`/api/v1/subsidies/check/${holdingId}`, { year }),
  });
  const data = (holdings.data ?? []).map((h) => ({ value: String(h.id), label: `${h.name} · ${h.bpg ?? ""}${h.opstina ? ` · ${h.opstina}` : ""}` }));
  const d = chk.data;
  const evaluated = d?.results.filter((r) => r.evaluator) ?? [];
  const info = d?.results.filter((r) => !r.evaluator) ?? [];
  return (
    <Stack>
      <Group align="flex-end">
        <Select label={t("Gazdinstvo")} placeholder={t("Pretraga po nazivu ili BPG")} searchable data={data} w={460}
          value={holdingId ? String(holdingId) : null} onChange={(v) => setHoldingId(v ? Number(v) : null)} nothingFoundMessage={t("Nema rezultata")} />
        {holdingId && <Button component={Link} to={`/gazdinstva/${holdingId}`} variant="subtle">{t("Karton gazdinstva")}</Button>}
      </Group>
      {!holdingId && <Empty text="Izaberite gazdinstvo — sistem provjerava uslove za podsticaje u voćarstvu iz podataka voćnjaka, poljoprivredne knjige i DMS-a." />}
      {chk.isLoading && <Text c="dimmed">{t("Provjera…")}</Text>}
      {chk.error && <Alert color="red">{errorMessage(chk.error)}</Alert>}
      {d && (
        <>
          <SimpleGrid cols={{ base: 1, md: 3 }}>
            <Card withBorder>
              <Text size="xs" c="dimmed">{t("Procjena premija i podsticaja (ispunjeni uslovi)")}</Text>
              <Text fw={800} size="xl">{fmtNum(d.estimate_total_km, 2)} KM</Text>
              <Text size="xs" c="dimmed">{t("Gornja granica kapitalnih investicija")}: {fmtNum(d.investment_ceiling_km, 0)} KM</Text>
            </Card>
            <Card withBorder>
              <Text size="xs" c="dimmed" mb={4}>{t("Površina po vrsti (ha)")}</Text>
              <Group gap={6}>{Object.entries(d.area_by_species).map(([k, v]) => <Badge key={k} variant="light">{k} {fmtNum(v, 2)}</Badge>)}</Group>
              <Text size="xs" c="dimmed" mt={6}>{t("Obračunski period prodaje")}: {fmtDate(d.period.od)} – {fmtDate(d.period.do)}</Text>
            </Card>
            <Card withBorder>
              <Text size="xs" c="dimmed" mb={4}>{t("Opšti uslovi")}</Text>
              <ChecksList checks={d.general} />
            </Card>
          </SimpleGrid>
          <Alert color="blue" variant="light" icon={<IconHelp size={16} />}>{t(d.disclaimer)}</Alert>
          <Title order={5}>{t("Mjere za voćarstvo sa provjerom uslova")}</Title>
          {evaluated.map((r) => <ResultCard key={r.code} r={r} year={year} holdingId={d.holding.id} />)}
          <Switch label={`${t("Prikaži i ostale mjere koje se odnose na voćarstvo (informativno)")} (${info.length})`}
            checked={showInfo} onChange={(e) => setShowInfo(e.currentTarget.checked)} />
          {showInfo && info.map((r) => <ResultCard key={r.code} r={r} year={year} holdingId={d.holding.id} />)}
        </>
      )}
    </Stack>
  );
}

// ------------------------------------------------------------------------------------------------ kartica: zahtjevi

function ApplicationModal({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["subsidies-app", id], queryFn: () => http.get<Application>(`/api/v1/subsidies/applications/${id}`) });
  const [v, setV] = useState<Partial<Application> | null>(null);
  const a = q.data;
  const cur = v ?? a ?? null;
  const manage = can("subsidies:manage");
  const save = useMutation({
    mutationFn: () => http.patch(`/api/v1/subsidies/applications/${id}`, manage ? {
      status: cur?.status, reg_no: cur?.reg_no || null, requested_km: cur?.requested_km ?? null, approved_km: cur?.approved_km ?? null, note: cur?.note || null,
    } : { reg_no: cur?.reg_no || null, requested_km: cur?.requested_km ?? null, note: cur?.note || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subsidies-apps"] }); qc.invalidateQueries({ queryKey: ["subsidies-check"] });
      notifications.show({ color: "green", message: t("Zahtjev sačuvan") }); onClose();
    },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });
  const del = useMutation({
    mutationFn: () => http.del(`/api/v1/subsidies/applications/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["subsidies-apps"] }); qc.invalidateQueries({ queryKey: ["subsidies-check"] }); onClose(); },
    onError: (e) => notifications.show({ color: "red", message: errorMessage(e) }),
  });
  return (
    <Modal opened onClose={onClose} size="xl" title={a ? `${t("Zahtjev")} #${a.id} — ${a.measure_code}` : t("Zahtjev")}>
      {!a || !cur ? <Text c="dimmed">{t("Učitavanje…")}</Text> : (
        <Stack gap="sm">
          <Text size="sm"><b>{a.holding_name}</b> · BPG {a.bpg} · {a.opstina} · {a.year}.</Text>
          <Text size="sm">{t(a.measure_name ?? "")}</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select label={t("Status")} disabled={!manage} value={cur.status}
              data={Object.entries(APP_STATUS).map(([k, x]) => ({ value: k, label: t(x.label) }))} onChange={(s) => s && setV({ ...cur, status: s })} />
            <TextInput label={t("Broj zahtjeva u Agenciji")} value={cur.reg_no ?? ""} onChange={(e) => setV({ ...cur, reg_no: e.currentTarget.value })} />
            <NumberInput label={t("Traženi iznos (KM)")} decimalSeparator="," thousandSeparator="." value={cur.requested_km ?? ""}
              onChange={(n) => setV({ ...cur, requested_km: typeof n === "number" ? n : undefined })} />
            <NumberInput label={t("Odobreni iznos (KM)")} disabled={!manage} decimalSeparator="," thousandSeparator="." value={cur.approved_km ?? ""}
              onChange={(n) => setV({ ...cur, approved_km: typeof n === "number" ? n : undefined })} />
          </SimpleGrid>
          <Textarea label={t("Napomena")} autosize minRows={2} value={cur.note ?? ""} onChange={(e) => setV({ ...cur, note: e.currentTarget.value })} />
          <Text size="xs" c="dimmed">{t("Podnesen")}: {fmtDate(a.submitted_at)} · {t("Odluka")}: {fmtDate(a.decided_at)} · {t("Evidentirao")}: {a.created_by ?? "—"}</Text>
          {a.check_result?.checks && (
            <Card withBorder>
              <Text fw={600} size="sm" mb={4}>{t("Provjera uslova u trenutku evidentiranja")}
                {a.check_result.status && <Badge ml="xs" color={STATUS[a.check_result.status]?.color}>{t(STATUS[a.check_result.status]?.label ?? "")}</Badge>}
                {a.check_result.estimate_km != null && <Text span size="sm"> · {fmtNum(a.check_result.estimate_km, 2)} KM</Text>}</Text>
              <ChecksList checks={[...(a.check_result.general ?? []), ...a.check_result.checks]} />
            </Card>
          )}
          <Group justify="space-between">
            {(a.status === "nacrt" || manage) ? <Button color="red" variant="subtle" loading={del.isPending} onClick={() => del.mutate()}>{t("Obriši")}</Button> : <span />}
            <Group>
              <Button variant="default" onClick={onClose}>{t("Zatvori")}</Button>
              {(manage || a.status === "nacrt") && <Button loading={save.isPending} onClick={() => save.mutate()}>{t("Sačuvaj")}</Button>}
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function ApplicationsTab({ year }: { year: number }) {
  const { t } = useT();
  const [status, setStatus] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const apps = useQuery({ queryKey: ["subsidies-apps", year, status], queryFn: () => http.get<Application[]>("/api/v1/subsidies/applications", { year, status: status ?? undefined }) });
  const rows = apps.data ?? [];
  return (
    <Stack>
      <Group>
        <Select placeholder={t("Svi statusi")} clearable w={220} value={status} onChange={setStatus}
          data={Object.entries(APP_STATUS).map(([k, x]) => ({ value: k, label: t(x.label) }))} />
        <Text size="sm" c="dimmed">{rows.length} {t("zahtjeva")} · {t("traženo")} {fmtNum(rows.reduce((s, r) => s + Number(r.requested_km ?? 0), 0), 2)} KM
          · {t("odobreno")} {fmtNum(rows.reduce((s, r) => s + Number(r.approved_km ?? 0), 0), 2)} KM</Text>
      </Group>
      <DataTable rows={rows as unknown as Record<string, unknown>[]} exportName={`podsticaji_zahtjevi_${year}`}
        onRowClick={(r) => setOpen(Number(r.id))} columns={[
          { key: "id", label: "#", type: "integer", width: 60 },
          { key: "measure_code", label: "Mjera", width: 80 },
          { key: "measure_name", label: "Naziv mjere" },
          { key: "holding_name", label: "Gazdinstvo" }, { key: "bpg", label: "BPG" }, { key: "opstina", label: "Opština" },
          { key: "status", label: "Status", render: (r) => <Badge size="sm" color={APP_STATUS[String(r.status)]?.color}>{t(APP_STATUS[String(r.status)]?.label ?? String(r.status))}</Badge> },
          { key: "requested_km", label: "Traženo KM", type: "money" }, { key: "approved_km", label: "Odobreno KM", type: "money" },
          { key: "updated_at", label: "Izmjena", type: "date" },
        ]} />
      {open && <ApplicationModal id={open} onClose={() => setOpen(null)} />}
    </Stack>
  );
}

// ------------------------------------------------------------------------------------------------ stranica

export default function SubsidiesPage() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const years = useYears();
  const tab = params.get("tab") ?? "mjere";
  const year = Number(params.get("godina") ?? THIS_YEAR);
  const holdingId = params.get("gazdinstvo") ? Number(params.get("gazdinstvo")) : null;
  const set = (k: string, v: string | null) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  return (
    <>
      <PageHeader title="Podsticaji" description="Novčani podsticaji Agencije za agrarna plaćanja RS prema Pravilniku: klasifikacija svih mjera po grupama i sektorima, provjera uslova za voćarstvo i evidencija zahtjeva."
        right={<>
          <Select size="xs" w={100} data={years} value={String(year)} onChange={(v) => v && set("godina", v)} aria-label={t("Godina")} />
          <Button size="xs" variant="subtle" component={Link} to="/uputstvo#g46-podsticaji" leftSection={<IconBook2 size={14} />}>{t("Uputstvo")}</Button>
        </>} />
      <Tabs value={tab} onChange={(v) => set("tab", v)} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="mjere" leftSection={<IconListSearch size={15} />}>{t("Mjere po grupama")}</Tabs.Tab>
          <Tabs.Tab value="provjera" leftSection={<IconFileCheck size={15} />}>{t("Provjera uslova (gazdinstvo)")}</Tabs.Tab>
          <Tabs.Tab value="zahtjevi" leftSection={<IconClipboardList size={15} />}>{t("Zahtjevi")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="mjere"><MeasuresTab year={year} /></Tabs.Panel>
        <Tabs.Panel value="provjera"><CheckTab year={year} holdingId={holdingId} setHoldingId={(v) => set("gazdinstvo", v ? String(v) : null)} /></Tabs.Panel>
        <Tabs.Panel value="zahtjevi"><ApplicationsTab year={year} /></Tabs.Panel>
      </Tabs>
    </>
  );
}
